(ns practitest.frontend.components.view-editor
  (:require
    [clojure.string :as str]
    [goog.functions :as gfunc]
    [malli.core :as m]
    [malli.error :as me]
    [practitest.frontend.common-entities.events :as common-events]
    [practitest.frontend.common-entities.metadata :as md]
    [practitest.frontend.common.fetch :refer [fetch-json]]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.modal :as modal]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.ptql-editor :as ptql-editor]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; ---- schema ----
(def FormSchema
  [:map {:closed true}
   [:name                     {:optional true} :string]
   [:name-auto-generated?     {:optional true} :boolean]
   [:public?                  {:optional true} :boolean]
   [:mode                     {:optional true} [:enum :ptql :auto-filter]]
   [:ancestors-ptql-raw       {:optional true} [:maybe :string]]
   [:ancestors-ptql-optimized {:optional true} [:maybe :string]]
   [:view-ptql                {:optional true} :string]
   [:combined-ptql-raw        {:optional true} [:maybe :string]]
   [:combined-ptql-optimized  {:optional true} [:maybe :string]]
   [:auto-filter              {:optional true} [:vector [:maybe :string]]]
   [:hide-empty-auto-filters  {:optional true} :boolean]])

(def EntityTypeSchema
  [:maybe [:enum "Test" "Issue" "Requirement" "TestSet"]])

(def AutoFilterOption
  [:map
   [:value :string]
   [:label :string]])

(def ViewEditorSchema
  [:map {:closed true}
   [:open?                                       :boolean]
   [:mode                       {:optional true} [:enum :create :edit]]
   [:display-modes              {:optional true}
    [:map
     [:ancestors {:optional true} [:enum :optimized :raw]]
     [:combined  {:optional true} [:enum :optimized :raw]]]]
   [:view-id                    {:optional true} [:maybe pos-int?]]
   [:parent-id                  {:optional true} [:maybe pos-int?]]
   [:entity-type                {:optional true} EntityTypeSchema]
   [:loading?                   {:optional true} :boolean]
   [:saving?                    {:optional true} :boolean]
   [:recomputing?               {:optional true} :boolean]
   [:error                      {:optional true} [:maybe :string]]
   [:auto-filter-options        {:optional true} [:vector AutoFilterOption]]
   [:auto-filter-max-levels     {:optional true} pos-int?]
   [:auto-filter-limit-reached? {:optional true} :boolean]
   [:form                       {:optional true} FormSchema]
   [:seed                       {:optional true} [:maybe FormSchema]]])

;; ---- interceptors ----

(def ^:private validate-state
  (re-frame/after
    (fn [db _event]
      (when-let [st (:view-editor db)]
        (when-not (m/validate ViewEditorSchema st)
          (js/console.error
            "Invalid :view-editor state after event:"
            (clj->js (me/humanize (m/explain ViewEditorSchema st)))))))))

(def ^:private event-interceptors
  (if ^boolean goog.DEBUG [validate-state] []))

;; ---- helpers ----

(def ^:private trigger-recompute!
  (gfunc/debounce
    #(re-frame/dispatch [::recompute!])
    400))

;; ---- effects & events

(re-frame/reg-fx
  ::http-fx
  (fn [{:keys [url method body on-success on-failure] :or {method "GET"}}]
    (-> (fetch-json url (cond-> {:method method}
                          body (assoc :body body)))
        (.then (fn [{:keys [ok? body]}]
                 (if ok?
                   (re-frame/dispatch (conj on-success body))
                   (re-frame/dispatch (conj on-failure body)))))
        (.catch (fn [err]
                  (re-frame/dispatch (conj on-failure {:error (.-message err)})))))))

(re-frame/reg-event-fx
  ::open-edit
  event-interceptors
  (fn [{db :db} [_ view-id]]
    (let [project-id (subs/get-current-project-id db)]
      {:db (assoc db :view-editor {:open? true
                                   :mode :edit
                                   :view-id view-id
                                   :loading? true})
       ::http-fx {:url (broutes/get-url "custom_views#editor_data"
                                        {:project_id project-id :id view-id})
                  :on-success [::loaded]
                  :on-failure [::load-failed]}})))

(re-frame/reg-event-fx
  ::open-create
  event-interceptors
  (fn [{db :db} [_ {:keys [parent-id entity-type seed]}]]
    (let [project-id (subs/get-current-project-id db)]
      {:db (assoc db :view-editor {:open? true
                                   :mode :create
                                   :parent-id parent-id
                                   :entity-type entity-type
                                   :seed seed
                                   :loading? true})
       ::http-fx {:url (broutes/get-url "custom_views#new_editor_data"
                                        {:project_id project-id}
                                        {:parent_id parent-id :entity_type entity-type})
                  :on-success [::loaded]
                  :on-failure [::load-failed]}})))

(re-frame/reg-event-fx
  ::loaded
  event-interceptors
  (fn [{db :db} [_ data]]
    (let [seed (get-in db [:view-editor :seed])
          base-form {:name (or (:name data) "")
                     :public? (boolean (:is_public data))
                     :mode (case (:mode data)
                             "auto_filter" :auto-filter
                             :ptql)
                     :ancestors-ptql-raw (:ancestors_ptql_raw data)
                     :ancestors-ptql-optimized (:ancestors_ptql_optimized data)
                     :view-ptql (or (:view_ptql data) "")
                     :combined-ptql-raw (:combined_ptql_raw data)
                     :combined-ptql-optimized (:combined_ptql_optimized data)
                     :auto-filter (vec (:auto_filter data))
                     :hide-empty-auto-filters (boolean (:hide_empty_auto_filters data))}
          new-db (-> db
                     (update :view-editor merge
                             {:loading? false
                              :entity-type (:entity_type data)
                              :display-modes {:ancestors :optimized :combined :optimized}
                              :parent-id (:parent_id data)
                              ;; XXX: do we really need to return the auto-filter options
                              ;;      (list of fields that can be used as auto-filter fields)
                              ;;      we have all the fields in frontend
                              :auto-filter-options (vec (:auto_filter_options data))
                              :auto-filter-max-levels (:auto_filter_max_levels data)
                              :auto-filter-limit-reached? (boolean (:auto_filter_limit_reached data))
                              :form (merge base-form seed)})
                     (update :view-editor dissoc :seed))]
      (cond-> {:db new-db}
        (not (str/blank? (:view-ptql seed)))
        (assoc :dispatch [::recompute!])))))

(re-frame/reg-event-db
  ::load-failed
  event-interceptors
  (fn [db _]
    (update db :view-editor merge
            {:loading? false
             :error (get-translation "view-editor.error.load-failed"
                                     "Failed to load view data")})))

(re-frame/reg-event-db
  ::close
  event-interceptors
  (fn [db _]
    (assoc db :view-editor {:open? false})))

(re-frame/reg-event-db
  ::name-changed
  event-interceptors
  (fn [db [_ name]]
    (-> db
        (assoc-in [:view-editor :form :name] name)
        (assoc-in [:view-editor :form :name-auto-generated?] false))))

(re-frame/reg-event-db
  ::public-changed
  event-interceptors
  (fn [db [_ value]]
    (assoc-in db [:view-editor :form :public?] value)))

(re-frame/reg-event-db
  ::set-display-mode
  event-interceptors
  (fn [db [_ section mode]]
    (assoc-in db [:view-editor :display-modes section] mode)))

(re-frame/reg-event-db
  ::set-mode
  event-interceptors
  (fn [db [_ mode]]
    (assoc-in db [:view-editor :form :mode] mode)))

(defn- replace-auto-filter-at [af idx value]
  (cond
    (str/blank? value) (vec (take idx af))
    (< idx (count af)) (assoc (vec af) idx value)
    :else (conj (vec af) value)))

(defn- auto-filter-name [labels]
  (str (get-translation "view-editor.auto-filter.name-prefix" "Auto Filter by")
       " " (str/join " / " labels)))

(re-frame/reg-event-db
  ::set-auto-filter-field
  event-interceptors
  (fn [db [_ idx value]]
    (let [form (get-in db [:view-editor :form])
          new-af (replace-auto-filter-at (or (:auto-filter form) []) idx value)
          rename? (and (seq new-af)
                       (or (:name-auto-generated? form)
                           (str/blank? (:name form))))
          opts-by-val (into {} (map (juxt :value :label))
                            (get-in db [:view-editor :auto-filter-options]))]
      (cond-> (assoc-in db [:view-editor :form :auto-filter] new-af)
        rename? (-> (assoc-in [:view-editor :form :name] (auto-filter-name (mapv #(opts-by-val % %) new-af)))
                    (assoc-in [:view-editor :form :name-auto-generated?] true))))))

(re-frame/reg-event-db
  ::add-auto-filter-level
  event-interceptors
  (fn [db _]
    (update-in db [:view-editor :form :auto-filter]
               (fn [af]
                 (conj (vec af) nil)))))

(re-frame/reg-event-db
  ::remove-auto-filter-level
  event-interceptors
  (fn [db [_ idx]]
    (update-in db [:view-editor :form :auto-filter]
               (fn [af]
                 (vec (take idx (or af [])))))))

(re-frame/reg-event-db
  ::set-hide-empty-auto-filters
  event-interceptors
  (fn [db [_ value]]
    (assoc-in db [:view-editor :form :hide-empty-auto-filters] value)))

(re-frame/reg-fx
  ::trigger-recompute-debounced
  (fn [_]
    (trigger-recompute!)))

(re-frame/reg-event-fx
  ::view-ptql-changed
  event-interceptors
  (fn [{db :db} [_ ptql]]
    {:db (assoc-in db [:view-editor :form :view-ptql] ptql)
     ::trigger-recompute-debounced true}))

(re-frame/reg-event-fx
  ::recompute!
  event-interceptors
  (fn [{db :db} _]
    (let [project-id (subs/get-current-project-id db)
          {:keys [parent-id entity-type]} (:view-editor db)
          view-ptql (get-in db [:view-editor :form :view-ptql])]
      {:db (assoc-in db [:view-editor :recomputing?] true)
       ::http-fx {:url (broutes/get-url "custom_views#recompute_combined"
                                        {:project_id project-id})
                  :method "POST"
                  :body {:parent_id parent-id
                         :entity_type entity-type
                         :view_ptql view-ptql}
                  :on-success [::recomputed]
                  :on-failure [::recompute-failed]}})))

(re-frame/reg-event-db
  ::recomputed
  event-interceptors
  (fn [db [_ {:keys [combined_ptql_raw combined_ptql_optimized]}]]
    (-> db
        (assoc-in [:view-editor :recomputing?] false)
        (assoc-in [:view-editor :form :combined-ptql-raw] combined_ptql_raw)
        (assoc-in [:view-editor :form :combined-ptql-optimized] combined_ptql_optimized))))

(re-frame/reg-event-db
  ::recompute-failed
  event-interceptors
  (fn [db _]
    ;; XXX: show error in UI?
    (assoc-in db [:view-editor :recomputing?] false)))

(re-frame/reg-event-fx
  ::save
  event-interceptors
  (fn [{db :db} _]
    (let [project-id (subs/get-current-project-id db)
          {:keys [mode view-id parent-id entity-type]} (:view-editor db)
          form (get-in db [:view-editor :form])
          create? (= mode :create)
          auto-filter-mode? (= :auto-filter (:mode form))
          base-body (cond-> {:name (:name form)
                             :is_public (boolean (:public? form))
                             :parent_id parent-id}
                      create? (assoc :entity_type entity-type))
          body (if auto-filter-mode?
                 (assoc base-body
                        :auto_filter (vec (remove str/blank? (:auto-filter form)))
                        :hide_empty_auto_filters (boolean (:hide-empty-auto-filters form)))
                 (assoc base-body :ptql (:view-ptql form)))]
      {:db (assoc-in db [:view-editor :saving?] true)
       ::http-fx {:url (broutes/get-url
                         (if create? "custom_views#create_view_editor" "custom_views#update_view_editor")
                         (cond-> {:project_id project-id}
                           (not create?) (assoc :id view-id)))
                  :method (if create? "POST" "PATCH")
                  :body body
                  :on-success [::saved]
                  :on-failure [::save-failed]}})))

(re-frame/reg-event-fx
  ::saved
  event-interceptors
  (fn [{db :db} [_ resp]]
    (if (= (:status resp) "ok")
      (let [entity-type (get-in db [:view-editor :entity-type])
            entity-kw (md/entity-by-module entity-type)
            saved-id (:id resp)]
        {:db (-> db
                 (assoc :view-editor {:open? false})
                 (assoc :new-filter-id (str saved-id "-" (.getTime (js/Date.)))))
         :dispatch [::common-events/activate-view entity-kw saved-id]})
      {:db (update db :view-editor merge
                   {:saving? false
                    :error (or (:message resp) "Save failed")})})))

(re-frame/reg-event-db
  ::save-failed
  event-interceptors
  (fn [db [_ resp]]
    (update db :view-editor merge
            {:saving? false
             :error (or (:message resp)
                        (get-translation "view-editor.error.save-failed"
                                         "Failed to save"))})))

;; ---- subs ----

(re-frame/reg-sub ::state (fn [db _] (:view-editor db)))
(re-frame/reg-sub ::open? :<- [::state] (fn [s _] (boolean (:open? s))))
(re-frame/reg-sub ::mode :<- [::state] (fn [s _] (:mode s)))
(re-frame/reg-sub ::entity-type :<- [::state] (fn [s _] (:entity-type s)))
(re-frame/reg-sub ::display-mode :<- [::state] (fn [s [_ section]] (get-in s [:display-modes section] :optimized)))
(re-frame/reg-sub ::loading? :<- [::state] (fn [s _] (boolean (:loading? s))))
(re-frame/reg-sub ::saving? :<- [::state] (fn [s _] (boolean (:saving? s))))
(re-frame/reg-sub ::recomputing? :<- [::state] (fn [s _] (boolean (:recomputing? s))))
(re-frame/reg-sub ::error :<- [::state] (fn [s _] (:error s)))
(re-frame/reg-sub ::auto-filter-options :<- [::state] (fn [s _] (:auto-filter-options s)))
(re-frame/reg-sub ::auto-filter-max-levels :<- [::state] (fn [s _] (:auto-filter-max-levels s)))
(re-frame/reg-sub ::auto-filter-limit-reached? :<- [::state] (fn [s _] (boolean (:auto-filter-limit-reached? s))))
(re-frame/reg-sub ::form :<- [::state] (fn [s _] (:form s)))

;; ---- views ----

(defn- tooltip-icon [text]
  [tooltip/mux-tooltip
   {}
   text
   (fn [props]
     [:span.view-editor-tooltip-icon props
      [icons/mux-info-circle-nofill]])])

(defn- mode-toggle [section]
  (let [mode @(re-frame/subscribe [::display-mode section])]
    [:span.view-editor-ptql-display-toggle
     (for [[v label] [[:optimized (get-translation "view-editor.ptql-mode.optimized" "optimized")]
                      [:raw (get-translation "view-editor.ptql-mode.raw" "raw")]]]
       ^{:key v}
       [buttons/button
        (cond-> {:size "small"
                 :text label
                 :on-click #(re-frame/dispatch [::set-display-mode section v])}
          (= mode v) (assoc :primary? true)
          (not= mode v) (assoc :tertiary? true))])]))

(defn- ptql-section [{:keys [label label-extra value on-change read-only? auto-focus? source-entity]}]
  (let [fields-by-entity @(re-frame/subscribe [::subs/fields-by-ptql-entity])
        users @(re-frame/subscribe [::subs/users])
        groups @(re-frame/subscribe [::subs/groups])]
    [:div.view-editor-section
     {:class (if read-only? "is-read-only" "is-editable")}
     (when (or label label-extra)
       [:div.view-editor-section-header
        (when label [:label.view-editor-section-label label])
        (when label-extra label-extra)])
     [ptql-editor/editor
      {:value value
       :on-change on-change
       :fields-by-entity fields-by-entity
       :source-entity source-entity
       :users users
       :groups groups
       :read-only? read-only?
       :auto-focus? auto-focus?}]]))

(defn- auto-filter-row [idx value options removable?]
  [:div.auto-filter-row
   (when (pos? idx)
     [:span.auto-filter-row-label
      (get-translation "view-editor.auto-filter.child-label" "Child auto-filter:")])
   [:select.form-control
    {:value (or value "")
     :on-change #(re-frame/dispatch [::set-auto-filter-field idx (-> % .-target .-value)])}
    [:option {:value ""} (get-translation "view-editor.auto-filter.none" "(select a field)")]
    (for [{:keys [value label]} options]
      ^{:key value} [:option {:value value} label])]
   (when removable?
     [buttons/icon-button
      {:icon [icons/mux-x]
       :tooltip (get-translation "view-editor.auto-filter.remove" "Remove")
       :on-click #(re-frame/dispatch [::remove-auto-filter-level idx])}])])

(defn- auto-filter-section [form]
  (let [options @(re-frame/subscribe [::auto-filter-options])
        max-levels @(re-frame/subscribe [::auto-filter-max-levels])
        levels (or (:auto-filter form) [])
        levels (if (empty? levels) [nil] levels)
        last-filled? (not (str/blank? (last levels)))
        can-add? (and last-filled? (< (count levels) max-levels))]
    [:div.view-editor-section.is-editable
     [:label.view-editor-section-label
      (get-translation "view-editor.auto-filter.section-label"
                       "Auto-filter fields")]
     (doall
       (map-indexed
         (fn [idx v]
           ^{:key idx} [auto-filter-row idx v options (and (pos? idx) (= idx (dec (count levels))))])
         levels))
     (when can-add?
       [buttons/button
        {:tertiary? true
         :icon [icons/mux-plus-circle]
         :text (get-translation "view-editor.auto-filter.add-child" "Add child auto-filter")
         :on-click #(re-frame/dispatch [::add-auto-filter-level])}])
     [:div.auto-filter-hide-empty
      [:label
       [:input {:type "checkbox"
                :checked (boolean (:hide-empty-auto-filters form))
                :disabled (not last-filled?)
                :on-change #(re-frame/dispatch [::set-hide-empty-auto-filters (-> % .-target .-checked)])}]
       " " (get-translation "view-editor.auto-filter.hide-empty" "Hide empty values")
       [tooltip-icon
        (get-translation "view-editor.auto-filter.hide-empty.tooltip"
                         "Don't show auto-filter values that have no matching entities.")]]]]))

(defn- view-editor-body []
  (let [form @(re-frame/subscribe [::form])
        loading? @(re-frame/subscribe [::loading?])
        recomputing? @(re-frame/subscribe [::recomputing?])
        mode @(re-frame/subscribe [::mode])
        source-entity @(re-frame/subscribe [::entity-type])
        limit-reached? @(re-frame/subscribe [::auto-filter-limit-reached?])
        af-mode? (= :auto-filter (:mode form))
        af-toggle-disabled? (and limit-reached? (not af-mode?))]
    (if loading?
      [:div.view-editor-loading
       (get-translation "view-editor.loading" "Loading...")]
      [:div.view-editor-body
       [:div.view-editor-name-row
        [:label {:for "view-editor-name"}
         (get-translation "view-editor.name" "Name")]
        [:input.form-control
         {:id "view-editor-name"
          :type "text"
          :auto-focus (= mode :create)
          :value (:name form)
          :on-change #(re-frame/dispatch [::name-changed (-> % .-target .-value)])}]]
       [:div.view-editor-public-row
        [:label
         [:input {:type "checkbox"
                  :checked (boolean (:public? form))
                  :on-change #(re-frame/dispatch [::public-changed (-> % .-target .-checked)])}]
         " "
         (get-translation "view-editor.is-public" "Public (visible to all users)")]]
       [:div.view-editor-filter-type
        [:label.view-editor-filter-type-label
         (get-translation "view-editor.filter-type" "Filter type")
         [tooltip-icon
          (get-translation "view-editor.filter-type.tooltip"
                           "Manual: define criteria using PTQL. Auto Filter: automatically generate categories for each value of a list field.")]]
        [:div.view-editor-filter-type-tabs
         (for [[m label] [[:ptql (get-translation "view-editor.filter-type.manual" "Manual")]
                          [:auto-filter (get-translation "view-editor.filter-type.auto-filter" "Auto Filter")]]]
           ^{:key m}
           [buttons/button
            (cond-> {:text label
                     :on-click #(re-frame/dispatch [::set-mode m])}
              (and (= m :auto-filter) limit-reached? (not af-mode?))
              (assoc :disabled? true)

              (not= (or (:mode form) :ptql) m)
              (assoc :tertiary? true))])]]
       (when af-toggle-disabled?
         [:div.view-editor-filter-type-warning
          (get-translation "view-editor.auto-filter.limit-reached"
                           "Auto-filter limit reached for this entity type")])
       (when-not (str/blank? (:ancestors-ptql-optimized form))
         (let [display-mode @(re-frame/subscribe [::display-mode :ancestors])
               value (case display-mode
                       :raw (:ancestors-ptql-raw form)
                       (:ancestors-ptql-optimized form))]
           [:details.view-editor-ancestors-details
            [:summary (get-translation "view-editor.ancestors.toggle" "Show inherited filters")]
            [ptql-section
             {:label nil
              :label-extra [mode-toggle :ancestors]
              :value value
              :read-only? true
              :source-entity source-entity}]]))
       (if af-mode?
         [auto-filter-section form]
         [:<>
          [ptql-section
           {:label (get-translation "view-editor.view-ptql" "This view's filter")
            :value (:view-ptql form)
            :read-only? false
            :auto-focus? (= mode :edit)
            :on-change #(re-frame/dispatch [::view-ptql-changed %])
            :source-entity source-entity}]
          (let [display-mode @(re-frame/subscribe [::display-mode :combined])
                value (case display-mode
                        :raw (:combined-ptql-raw form)
                        (:combined-ptql-optimized form))]
            [ptql-section
             {:label (if recomputing?
                       (get-translation "view-editor.combined-computing" "Effective filter (computing...)")
                       (get-translation "view-editor.combined" "Effective filter (read-only)"))
              :label-extra [mode-toggle :combined]
              :value (or value "")
              :read-only? true
              :source-entity source-entity}])])])))

(defn view-editor-modal []
  (let [open? @(re-frame/subscribe [::open?])
        mode @(re-frame/subscribe [::mode])
        saving? @(re-frame/subscribe [::saving?])
        form @(re-frame/subscribe [::form])
        af-mode? (= :auto-filter (:mode form))
        error @(re-frame/subscribe [::error])]
    [modal/bootstrap-modal
     {:is-open? open?
      :on-request-close #(re-frame/dispatch [::close])
      :close-button? true
      :centered? true
      :scrollable? true
      :class "view-editor-modal"}
     [:h2 (if (= mode :create)
            (get-translation "view-editor.create-title" "Create view")
            (get-translation "view-editor.edit-title" "Edit view"))]
     [:<>
      (when error [:div.view-editor-error error])
      [view-editor-body]]
     [:div.view-editor-footer
      [buttons/button {:text (get-translation "view-editor.cancel" "Cancel")
                       :on-click #(re-frame/dispatch [::close])}]
      [buttons/button {:primary? true
                       :text (if saving?
                               (get-translation "view-editor.saving" "Saving...")
                               (get-translation "view-editor.save" "Save"))
                       :disabled? (or saving?
                                      (str/blank? (:name form))
                                      (and af-mode?
                                           (empty? (remove str/blank? (:auto-filter form)))))
                       :on-click #(re-frame/dispatch [::save])}]]]))

(comment
  (broutes/get-url "custom_views#new_editor_data" {:project_id 30} {:parent_id nil :entity_type "Issue"})
  (re-frame/dispatch [::open-edit 21625])

  (re-frame/dispatch [::open-create {:parent-id 82051
                                     :entity-type "Issue"
                                     :seed {:name "Test seed name 4"
                                            :view-ptql "Priority = \"High\""}}])

  )
